/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import { BuildingColDefs } from "../columnDefs";
import CommonModule from "../CommonModule";
import FilterPanel from "./FilterPanel";
import { apiRequest } from "../../../../common/common";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { LoadingPage } from "../../../../common/LoadingPage";
import { Box, Button, Dialog, DialogActions, DialogContent, Divider, Grid, Link, styled, Switch, Tooltip, Typography } from "@mui/material";
import { BuildingType, CurrencyJSON, getProjectSize } from "../../../../common/const";
import { ArrowBack, DeleteOutline, EditOutlined } from "@mui/icons-material";
import CreateModule from "../CreateModule";
import { useSelector } from "react-redux";
import ColumnPanel from "../../../../common/ColumnPanel";
import * as XLSX from 'xlsx';

const DemoTypo = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  textAlign: 'justify'
}));

const DemoTypo1 = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 'bold'
}));

const BuildingModule = ({
  projectId,
  projectData,
  setActiveTab,
  setBuilding
}) => {
  const { user, roles } = useSelector((state) => state.users);
  const isUser = roles.find((a) => a.id === user?.role_id);
  const [gridApi, setGridApi] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [rowsPerPage, setRowPerPage] = useState(localStorage.getItem('buildingPageSize') || 10);
  const [searchingText, setSearchText] = useState(null || localStorage.getItem('search'));
  const [selectedData, setSelectedData] = useState([]);
  const [notes, setNotes] = useState(null);
  const [buildingId, setBuildingId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [buildingData, setBuildingData] = useState(false);
  const [buildingColumn, setBuildingColumn] = useState(BuildingColDefs);
  const [settingData, setSettingData] = useState([]);
  const [countData, setCountData] = useState({});

  const onGridReady = useCallback((params) => {
    if (params?.api) {
      const { api, searchingText, filters } = params;
      if (filters) {
        localStorage.setItem('buildingfilters', JSON.stringify(filters));
      }
      const filter = localStorage.getItem('buildingfilters') ? JSON.parse(localStorage.getItem('buildingfilters')) : filters;
      setGridApi(api);
      setDataLoading(true);
      const dataSource = {
        rowCount: null,
        getRows: async (params) => {
          const sortModel = params?.sortModel[0];
          const sortBy = {
            column: sortModel?.colId || '',
            order: sortModel?.sort || '',
          };
          if (projectId) {
            const url = [
              `/building?projectId=${projectId}`,
              `pageNo=${Math.floor((params.startRow / rowsPerPage) + 1)}`,
              `pageSize=${rowsPerPage}`,
              filter?.buildingType?.length > 0 ? `type=${filter?.buildingType}` : '',
              filter?.isDeleted === 'yes' || filter?.isDeleted === 'no' ? `isDeleted=${filter?.isDeleted === 'yes' ? true : ''}` : '',
              sortBy?.column ? `sortBy=${sortBy.column}` : '',
              sortBy?.order ? `sortOrder=${sortBy.order}` : '',
              searchingText || localStorage.getItem('search') ? `searchText=${encodeURIComponent(searchingText || localStorage.getItem('search'))}` : ''
            ].filter(Boolean).join('&');
            try {
              const response = await apiRequest('get', `${url}`);
              const { data, total_count, total_cost, total_area, total_building_count } = response.data;
              let lastRow = -1;
              if (data?.length <= total_count) {
                lastRow = total_count;
              }
              setCountData({
                total_area,
                total_quantity: total_building_count,
                total_cost
              });
              params.successCallback(data, lastRow);
              setDataLoading(false);
            } catch (error) {
              setDataLoading(false);
              showNotification(error?.response?.data.error || error.message, 'error');
            }
          }
        },
      };
      api.setGridOption("datasource", dataSource);
      setTimeout(() => {
        const node = api.getRenderedNodes();
        if (!node?.[0]?.displayed) {
          api.showNoRowsOverlay();
        }
      }, 5000);
    }
  }, [rowsPerPage, projectId]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      onGridReady({
        api: gridApi,
      });
    }
    return () => {
      isMounted = false;
    };
  }, [rowsPerPage, projectId]);

  useEffect(() => {
    if (buildingColumn?.length > 0 && gridApi) {
      const columnState = gridApi?.getColumnState()?.map(state => {
        const columnConfig = buildingColumn.find((c) => c.colId === state.colId || c.field === state.colId);
        if (columnConfig) {
          if (columnConfig.pinned !== state.pinned) {
            state.pinned = columnConfig.pinned ? columnConfig.pinned : null;
          }
        }

        return state;
      });
      gridApi?.applyColumnState({ state: columnState });
      gridApi?.refreshHeader();
    }
  }, [buildingColumn]);


  const getData = async () => {
    try {
      setDataLoading(true)
      const response = await apiRequest('get', `/user-view?userId=${user?.user_id}&type=project-building&pageNo=1&pageSize=50`);
      const { data } = response.data;
      if (data?.length > 0) {
        if (data?.find((a) => a.is_pinned === true)?.columns) {
          setBuildingColumn([...buildingColumn.slice(0, 3), ...data?.find((a) => a.is_pinned === true)?.columns]);
        }
        setSettingData(data);
      } else {
        setSettingData([]);
      }
      setDataLoading(false);
    } catch (error) {
      setDataLoading(false);
      showNotification(error?.response?.data.error || error.message, 'error');
    }
  }

  useEffect(() => {
    if (user?.user_id) {
      getData();
    }
  }, [user?.user_id]);

  const handleExportExcel = async () => {
    try {
      setDataLoading(true);
      const url = [`/building?projectId=${projectId}`, `pageNo=1`, `pageSize=10000`].filter(Boolean).join('&');
      const response = await apiRequest('get', url);
      const { data } = response.data;
      const currencySymbol = CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol ?? '';
      const exportRows = (data || []).map((row) => ({
        'Building No': row.building_no ?? '--',
        'Building Name': row.building_name ?? '--',
        'Type': BuildingType.find((a) => a.value === row.type)?.name ?? '--',
        'Status': row.is_deleted === false ? 'Active' : 'InActive',
        'Total Levels': row.active_level_count ?? 0,
        'Inactive Levels': row.inactive_level_count ?? 0,
        'Total Rooms': row.active_room_count ?? 0,
        'Inactive Rooms': row.inactive_room_count ?? 0,
        'Combined Rooms': row.all_room_count ?? 0,
        'Total Area': row.total_area ? getProjectSize(projectData?.default_project_units, row.total_area) : '0',
        'Sub Total': `${currencySymbol} ${Intl.NumberFormat('en-IN').format(row.sub_total || 0)}`,
        'Total Cost': `${currencySymbol} ${Intl.NumberFormat('en-IN').format(row.building_total_amount || 0)}`,
        'Notes': row.notes ?? '--',
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      ws['!cols'] = Object.keys(exportRows[0] || {}).map((key) => ({
        wch: Math.max(key.length, ...exportRows.map((r) => String(r[key] ?? '').length)) + 2,
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Buildings');
      XLSX.writeFile(wb, `Buildings_${projectId}.xlsx`);
    } catch (error) {
      showNotification(error?.response?.data?.error || error.message, 'error');
    } finally {
      setDataLoading(false);
    }
  };

  const toolPanel = useMemo(() => [
    {
      id: 'custom1',
      labelDefault: 'Columns',
      iconKey: "columns",
      toolPanel: ColumnPanel,
      width: 450,
      toolPanelParams: {
        columns: buildingColumn?.slice(3, 16),
        setColumns: (column) => {
          setBuildingColumn([...buildingColumn.slice(0, 3), ...column]);
        },
        settingData,
        fetchData: () => {
          if (user?.user_id) {
            getData();
          }
        },
        type: "project-building",
        defaultVisibleColumns: BuildingColDefs?.slice(3, 16),
        columnOrder: buildingColumn?.slice(3, 16),
      }
    },
    {
      id: 'custom2',
      iconKey: "filter",
      labelDefault: 'Filters',
      toolPanel: FilterPanel,
      width: 300,
      toolPanelParams: {
        applyFilter: (filters) => {
          setRowPerPage(localStorage.getItem('buildingPageSize') || 10)
          onGridReady({
            api: gridApi,
            filters
          });
        }
      }
    }
  ], [gridApi, settingData, buildingColumn]);

  const updatedColumns = useMemo(() => {
    let column = buildingColumn;
    if (column.length > 0) {
      return column?.map((col) => {
        switch (col.field) {
          case '': {
            return {
              ...col,
              hide: isUser?.code === 'orgGuestUser'
            }
          }
          case 'action':
            return {
              ...col,
              hide: isUser?.code === 'orgGuestUser',
              cellRenderer: (params) => params?.data && (
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  {!params?.data?.is_deleted && (
                    <Tooltip title="Edit Building">
                      <EditOutlined
                        color="primary"
                        style={{ cursor: 'pointer', color: selectedData?.includes(params?.data?.building_id) ? '' : 'gray' }}
                        onClick={() => {
                          if (selectedData?.includes(params?.data?.building_id)) {
                            setOpen(true);
                            setBuildingData(params?.data);
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title={params?.data?.is_deleted === false ? 'Active' : 'InActive'}>
                    <Switch
                      size="small"
                      disabled={!selectedData?.includes(params?.data?.building_id)}
                      checked={params?.data?.is_deleted === false}
                      onChange={async (e) => {
                        setDataLoading(true);
                        try {
                          const response = await apiRequest('patch', `/building/set-delete-status/${params?.data.building_id}`, {
                            isDeleted: e.target.checked === true ? false : true
                          });
                          if (response?.data) {
                            onGridReady({
                              api: gridApi
                            })
                            setDataLoading(false);
                            showNotification(response?.data?.message, 'success');
                          }
                        } catch (error) {
                          setDataLoading(false);
                          showNotification(error?.response?.data.error || error.message, 'error');
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Permanent Delete">
                    <DeleteOutline
                      color="secondary"
                      style={{ cursor: 'pointer', color: selectedData?.includes(params?.data?.building_id) ? '' : 'gray' }}
                      onClick={() => {
                        if (selectedData?.includes(params?.data?.building_id)) {
                          setDeleteOpen(true);
                          setBuildingId(params?.data?.building_id);
                        }
                      }}
                    />
                  </Tooltip>
                </Box>
              ),
            }
          case 'building_name':
            return {
              ...col,
              cellRenderer: (params) => {
                if (params?.data && !params?.data.is_deleted) {
                  return (
                    <Link
                      style={{ textDecoration: 'none', cursor: 'pointer' }}
                      onClick={() => {
                        setActiveTab('Levels');
                        setBuilding(params?.data);
                      }}
                    >

                      {params?.data?.building_name || '--'}
                    </Link>
                  )
                } else {
                  return (
                    <span style={{ color: 'red' }}>{params?.data?.building_name || '--'}</span>
                  )
                }
              },
            }
          case 'type':
            return {
              ...col,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {BuildingType.find((a) => a.value === params?.data?.type)?.name || '--'}
                </>
              ),
            }
          case 'status':
            return {
              ...col,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {!params?.data?.is_deleted ? 'Active' : 'InActive'}
                </>
              ),
            }
          case 'total_cost':
            return {
              ...col,
              minWidth: 175,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(params?.data?.building_total_amount || 0)}`}
                </>
              ),
            }
          case 'subtotal':
            return {
              ...col,
              minWidth: 175,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {`${CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol} ${Intl.NumberFormat('en-IN').format(params?.data?.sub_total || 0)}`}
                </>
              ),
            }
          case 'building_no':
            return {
              ...col,
              minWidth: 90,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.building_no}
                </>
              ),
            }
          case 'total_level':
            return {
              ...col,
              minWidth: 80,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.active_level_count || 0}
                </>
              ),
            }
          case 'total_inactive_level':
            return {
              ...col,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.inactive_level_count || 0}
                </>
              ),
            }
          case 'total_rooms':
            return {
              ...col,
              minWidth: 80,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.active_room_count || 0}
                </>
              ),
            }
          case 'total_inactive_rooms':
            return {
              ...col,
              minWidth: 110,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.inactive_room_count || 0}
                </>
              ),
            }
          case 'total_combind_rooms':
            return {
              ...col,
              minWidth: 130,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.all_room_count || 0}
                </>
              ),
            }
          case 'total_area':
            return {
              ...col,
              minWidth: 120,
              flex: 1,
              cellStyle: (params) => {
                if (params?.data?.is_deleted) {
                  return {
                    color: 'red'
                  }
                }
                return {
                  background: params.node.rowIndex % 2 === 0 ? 'white' : '#d8e2e5',
                };
              },
              cellRenderer: (params) => params?.data && (
                <>
                  {params?.data?.total_area !== null && params?.data?.total_area ? getProjectSize(projectData?.default_project_units, (params?.data?.total_area)) : '0'}
                </>
              ),
            }
          case 'notes':
            return {
              ...col,
              minWidth: 110,
              flex: 1,
              cellRenderer: (params) => params?.data?.notes ? (
                <Link
                  style={{ textDecoration: 'none', cursor: 'pointer' }}
                  onClick={() => {
                    setNotes(params?.data.notes);
                  }}
                >
                  View Notes
                </Link>
              ) : '--',
            }
          default:
            return col;
        }
      });
    }
    return column;
  }, [buildingColumn, selectedData]);

  return (
    <>
      {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
      <Dialog open={deleteOpen} fullWidth maxWidth="xs">
        <DialogContent>Are you sure you want to permanently delete this one? It cannot be recovered.</DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" color="secondary"
            onClick={() => {
              setBuildingId(null);
              setDeleteOpen(false);
            }}
          >
            No
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={async () => {
              try {
                setDataLoading(true);
                let response = await apiRequest('delete', `/building/${buildingId}`);
                if (response?.data) {
                  setDataLoading(false);
                  showNotification(response?.data?.message, 'success');
                  setBuildingId(null);
                  setDeleteOpen(false);
                  onGridReady({
                    api: gridApi
                  });
                }
              } catch (error) {
                setDataLoading(false);
                showNotification(error?.response?.data.error || error.message, 'error');
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {open && (
        <CreateModule
          open={open}
          setOpen={setOpen}
          getData={() => onGridReady({
            api: gridApi
          })}
          innerLabel="Building"
          projectId={projectId}
          initialData={buildingData}
        />
      )}
      <Grid container>
        <Grid item xs={12} sm={12} md={notes ? 9 : 12} style={{ padding: '0px 8px 0px 8px' }}>
          <CommonModule
            label="Buildings"
            innerLabel="Building"
            columnDefs={updatedColumns}
            onGridReady={onGridReady}
            rowsPerPage={rowsPerPage}
            setRowPerPage={setRowPerPage}
            searchingText={searchingText}
            setSearchText={setSearchText}
            toolPanel={toolPanel}
            projectId={projectId}
            gridApi={gridApi}
            onSelectionChanged={() => {
              if (gridApi) {
                const selectedNodes = gridApi.getSelectedNodes();
                setSelectedData(selectedNodes.map((node) => node.data.building_id));
              }
            }}
            projectData={projectData}
            selectionType="single"
            countData={countData}
            setSelectedData={setSelectedData}
            setColumns={setBuildingColumn}
            onExport={handleExportExcel}
          />
        </Grid>
        {notes !== null && (
          <Grid item xs={12} sm={12} md={3} style={{ borderLeft: '1px solid lightgray' }}>
            <Box mt={1} mb={1} ml={1}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 5 }} mb={1}>
                <ArrowBack style={{ cursor: 'pointer', fontSize: 20 }} onClick={() => {
                  setNotes(null);
                }} />
                <DemoTypo1 variant="body2">Notes:</DemoTypo1>
              </Box>
              <Divider />
              <Box style={{ height: '57vh', overflow: 'auto' }} mt={1}>
                <DemoTypo variant="body2">{notes}</DemoTypo>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default BuildingModule;